'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { formatUsd, useCart, type CartItem } from '@/lib/cart'
import { collectCheckoutAttribution, startCheckout, type CheckoutFailureReason } from '@/lib/checkout'
import styles from './CartDrawer.module.css'

/**
 * The bag drawer (R-BRAND-02, R-ARCH-06). Mounted once in the site layout and
 * rendered only while open, so the closed state contributes nothing to the
 * document. Open, it is a modal dialog: labelled by its heading, focus moves to
 * the close control on open and returns to the opener on close, Tab cycles
 * inside, Escape and the backdrop close it, and body scroll is locked. Rows show
 * what the buyer chose — title, engraving and box-signature lines, quantity and
 * line total — and the seller disclosure sits with the Checkout action.
 * Checkout posts the bag to the same-origin session route; 503 renders the
 * "not open yet" posture instead of an error.
 */
export const SELLER_DISCLOSURE = 'Sold and fulfilled by PRNTD. Payment is processed securely by Stripe.'
export const EMPTY_BAG_COPY = 'Your bag is empty.'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

type CheckoutPhase = { kind: 'idle' } | { kind: 'loading' } | { kind: 'failed'; reason: CheckoutFailureReason; message: string }

export default function CartDrawer() {
  const { items, subtotalCents, open, remove, setOpen } = useCart()
  if (!open) return null
  return <DrawerPanel items={items} subtotalCents={subtotalCents} onRemove={remove} onClose={() => setOpen(false)} />
}

type PanelProps = {
  readonly items: readonly CartItem[]
  readonly subtotalCents: number
  readonly onRemove: (id: string) => void
  readonly onClose: () => void
}

function DrawerPanel({ items, subtotalCents, onRemove, onClose }: PanelProps) {
  const headingId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const [phase, setPhase] = useState<CheckoutPhase>({ kind: 'idle' })

  // Focus management + scroll lock for the life of the open panel.
  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
      const opener = openerRef.current
      if (opener && opener.isConnected) opener.focus()
    }
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (focusable.length === 0) {
        event.preventDefault()
        panelRef.current.focus()
        return
      }
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      const active = document.activeElement
      if (event.shiftKey && (active === first || !panelRef.current.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || !panelRef.current.contains(active))) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const checkout = useCallback(async () => {
    if (items.length === 0) return
    setPhase({ kind: 'loading' })
    const result = await startCheckout(items, collectCheckoutAttribution())
    if (result.ok) return
    setPhase({ kind: 'failed', reason: result.reason, message: result.message })
  }, [items])

  const busy = phase.kind === 'loading'

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" data-testid="cart-backdrop" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={styles.panel}
        tabIndex={-1}
        data-cart-drawer=""
      >
        <div className={styles.header}>
          <div>
            <p className={styles.eyebrow}>MIST by BDQ</p>
            <h2 id={headingId} className={styles.title}>
              Your Cart
            </h2>
          </div>
          <button ref={closeRef} type="button" className={styles.close} onClick={onClose} aria-label="Close cart">
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <div className={styles.body}>
          {items.length === 0 ? (
            <p className={styles.empty}>{EMPTY_BAG_COPY}</p>
          ) : (
            <ul className={styles.list} aria-label="Items in your cart">
              {items.map((item) => (
                <li key={item.id} className={styles.item}>
                  <p className={styles.itemTitle}>{item.title}</p>
                  <p className={styles.itemTotal}>{formatUsd(item.unitAmountCents * item.quantity)}</p>
                  {item.details.length > 0 ? (
                    <ul className={styles.details}>
                      {item.details.map((detail, index) => (
                        <li key={`${item.id}-${index}`}>{detail}</li>
                      ))}
                    </ul>
                  ) : null}
                  <div className={styles.itemMeta}>
                    <span>
                      Qty {item.quantity} · {formatUsd(item.unitAmountCents)} each
                    </span>
                    <button type="button" className={styles.remove} onClick={() => onRemove(item.id)} aria-label={`Remove ${item.title}`}>
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.footer}>
          <p className={styles.subtotal}>
            <span>Subtotal</span>
            <span className={styles.subtotalAmount} data-cart-subtotal="">
              {formatUsd(subtotalCents)}
            </span>
          </p>
          <button
            type="button"
            className={`mist-button mist-button--primary ${styles.checkout}`}
            onClick={() => void checkout()}
            disabled={items.length === 0 || busy}
            aria-busy={busy || undefined}
          >
            {busy ? 'Opening checkout…' : 'Checkout'}
          </button>
          {phase.kind === 'failed' ? (
            <p className={styles.status} role={phase.reason === 'unavailable' ? 'status' : 'alert'} data-checkout-state={phase.reason}>
              {phase.message}
            </p>
          ) : null}
          <p className={styles.disclosure}>{SELLER_DISCLOSURE}</p>
        </div>
      </div>
    </>
  )
}
